import type { BridgeIntegrationId } from "@common/lib/api/bridge";
import type { BridgeUpgradeStage } from "@/state/hooks/useBridgeUpgradeOffer";
import { PIMS_COPY } from "@/features/bridge-setup/bridge-setup-config";

/**
 * The shared prose of the HappyBridge upgrade offer (EN-1063), and the helpers that name a clinic's
 * software.
 *
 * Prose shown by more than one surface lives here; prose shown by one stays in that component.
 * `bridgeUpgradeCopy.test.ts` scans everything this module exports, so moving a sentence here also
 * brings it under the copy guards — including the repo-wide rule that the third-party middleware is
 * never named to a clinic.
 *
 * Every claim must describe what ships TODAY. Three of the four do; the fourth is forward-looking
 * in the shape of its sentence ("built to", "over time") rather than naming an unshipped feature.
 *
 * Nothing here takes a PIMS *name* — callers pass the `BridgeIntegrationId` and each slot does its
 * own naming, because the cohort whose `current_pims` we cannot read has no bare noun. ImproMed
 * strings exist for the wizard, but the offer is not shown to ImproMed practices.
 */

/** HubSpot booking link. Opens in a new tab — no embed, no scheduler, no CSP change. */
export const BRIDGE_UPGRADE_BOOKING_URL = "https://meetings-na2.hubspot.com/emerson-smith/happybridge-install";

/**
 * The clinic's software as a bare noun: "Cornerstone", "AVImark", "ImproMed".
 *
 * NON-nullable on purpose. There is no honest bare noun for the unknown-PIMS cohort, so every slot
 * wanting one must write its own sentence for that case — the compiler enforces it, which is what
 * turned "The colors your team assigned in practice software show up…" into a type error.
 * For a slot that reads naturally either way, use `softwarePossessive`.
 */
export const pimsLabel = (integration: BridgeIntegrationId): string => PIMS_COPY[integration].name;

/**
 * The software in a possessive or object slot: "talks to Cornerstone itself" / "talks to your
 * practice software itself". The one naming helper that accepts the unknown case, because this slot
 * has an honest phrase for it.
 */
export const softwarePossessive = (integration: BridgeIntegrationId | null): string =>
    integration ? pimsLabel(integration) : "your practice software";

/**
 * The offer's opening line, shown identically by the app-wide strip (as its bold lead) and by the
 * announcement card (as its headline).
 *
 * ONE function rather than the same sentence typed into two components. The two surfaces were
 * making different opening claims — the strip announced an update, the card promised a direct
 * connection — and a clinic that saw the strip and clicked through read two offers for one
 * irreversible change. They now say the same thing, and this is what keeps that true: a reworded
 * lead reaches both surfaces or neither.
 *
 * Two whole sentences rather than one template with the name swapped in, for the same reason
 * `pimsLabel` takes a non-nullable id: the cohort whose `current_pims` we cannot read has no
 * bare noun, and `softwarePossessive` is no help in this slot — "your practice software
 * integration upgrade now available!" is not a sentence. That cohort gets its own wording.
 */
export const bridgeUpgradeLead = (integration: BridgeIntegrationId | null): string =>
    integration ? `${pimsLabel(integration)} integration upgrade now available!` : "Integration upgrade now available!";

/**
 * Which machine the bridge may live on: the server's name for a bold lead, and the rule beneath it.
 *
 * Both halves come from `PIMS_COPY` because the strictness differs per PIMS. "Will only install" is
 * a promise about what the INSTALLER refuses — true for Cornerstone (EN-990) and ImproMed, false
 * for AVImark, whose requirement follows from EN-979 detecting the RemObjects port: nothing blocks
 * the install, the bridge just never finds AVImark. This hardcoded the Cornerstone sentence for all
 * three, so AVImark clinics were promised a guard that does not exist.
 *
 * `machineRule` rather than `machineRequirement` because the latter names the machine inside
 * itself, which stutters under a lead that just said it. The two sit together in `PIMS_COPY` so
 * they cannot drift.
 *
 * The unknown-PIMS fallback takes the WEAKER verb on purpose. That branch is reached exactly when
 * `current_pims` is null or empty — which `useBridgeUpgradeOffer` treats as eligible, and which is
 * the largest slice of the cohort — so the clinic reading it may well be running AVImark. Promising
 * them a guard that will not fire is the same falsehood `machineRule` exists to prevent, and it
 * lands on the way into an irreversible connector flip.
 */
export const bridgeMachineRequirement = (
    integration: BridgeIntegrationId | null,
): { serverNoun: string; rule: string } =>
    integration
        ? {
              serverNoun: PIMS_COPY[integration].serverNoun,
              rule: PIMS_COPY[integration].machineRule,
          }
        : {
              serverNoun: "Your database server",
              rule: "HappyBridge has to run on the Windows computer where your practice software's database lives, not on a front desk machine.",
          };

/**
 * The offer's eyebrow, shared by the card and the modal at two weights — the modal is where a clinic
 * arrives after clicking the card, and two spellings of the product's name reads as two products.
 * Bare "HappyBridge" for the unknown cohort; "HappyBridge for your practice software" is templated.
 */
export const bridgeUpgradeEyebrow = (integration: BridgeIntegrationId | null): string =>
    integration ? `HappyBridge for ${pimsLabel(integration)}` : "HappyBridge";

/**
 * Introduces the product, shared by the card's body and the modal's lede so the modal reads as a
 * second page rather than a restart of the pitch.
 *
 * Says nothing about what they run today: eligibility does not require a live integration, and a
 * meaningful minority of the cohort has no integration row at all — so "your practice runs on a
 * third-party connector today" would be a fact we never checked, on the way into an irreversible
 * change.
 */
export const bridgeUpgradeIntro = (integration: BridgeIntegrationId | null): string =>
    integration
        ? `HappyBridge is HappyDoc's own PIMS connector, and it now supports ${pimsLabel(integration)}.`
        : "HappyBridge is HappyDoc's own PIMS connector for practices running their software on site.";

/**
 * The one-line argument, carried word for word by the strip and the card — one repeated phrase is
 * what makes them read as one offer. "Steadier" not "more reliable", for the reason above.
 */
export const BRIDGE_UPGRADE_TAGLINE =
    "A steadier connection with nothing in the middle, and new features land here first.";

/**
 * Which shared SVG illustrates this claim. A stable id rather than array position: the card and the
 * modal render this list separately, and index-pairing gave a claim the wrong icon on a reorder.
 */
export type BridgeUpgradeFeatureId = "reliability" | "sync" | "details" | "foundation";

export interface BridgeUpgradeFeature {
    /** Which shared icon illustrates this claim — see `bridgeUpgradeIcons.tsx`. */
    id: BridgeUpgradeFeatureId;
    /** Tile heading, both surfaces. */
    title: string;
    /** One line, for the card's compact tiles. */
    short: string;
    /** Fuller version, for the modal where there is room to earn the claim. */
    detail: string;
}

export const bridgeUpgradeFeatures = (integration: BridgeIntegrationId | null): BridgeUpgradeFeature[] => [
    {
        id: "reliability",
        // STRUCTURAL, not measured: fewer things in the path, stated as an architecture fact. Not
        // "more reliable than what you have today" — a comparative measures against a connection we
        // never checked, and a meaningful minority of the cohort has no integration row at all.
        title: "A steadier connection",
        short: "Nothing in between to be down when you need it.",
        detail: `HappyBridge talks to ${softwarePossessive(integration)} directly, so there is no service in between that can be down in the middle of your day.`,
    },
    {
        id: "sync",
        // NOT "every 5 minutes", which this claimed until a review checked it. That poll is
        // Cornerstone-only (`API-Server/app/control/cronRoutes.ts:67`) AND gated on
        // `happy_bridge_change_detection_enabled`, which 22 of 26 live-bridge clinics did not have
        // (2026-09-18). The automatic nightly pull is what every bridge account gets.
        title: "Appointments sync on their own",
        short: "Your schedule is waiting when you open HappyDoc.",
        detail: "The bridge brings your schedule over for you, so the day's appointments are already there.",
    },
    {
        id: "details",
        // Color codes are the one small-detail claim checked against a running bridge, so it is
        // the one named. The broader promise stays unenumerated: the only `happy_bridge_*` columns
        // are internal rollout flags, not settings a practice can see.
        title: "The details carry over",
        short: "Your color codes come across with the schedule.",
        detail: `The colors your team assigned in ${softwarePossessive(integration)} show up on the HappyDoc schedule too, so it reads the way your team set it up.`,
    },
    {
        id: "foundation",
        // The forward-looking tile, written to describe what the connector is BUILT FOR rather
        // than to promise dated features — it sits above a button that starts an irreversible flip.
        // Writeback beyond the patient record is a real intention with no code in any repo yet.
        title: "Built to keep growing",
        short: "A modern connector we can keep building on.",
        detail: `HappyBridge is ours end to end, which makes it the foundation for moving more of the record between HappyDoc and ${softwarePossessive(integration)} over time. New integration work lands here first.`,
    },
];

/**
 * EN-1333. What the offer says once the clinic has started: a task to finish, not a pitch. No
 * "New", no "now available". No step count either, because the remaining path differs by PIMS
 * (Cornerstone's suite step vs calendar, staff names and sync) and a wrong number is worse than
 * none. "Finish setting up", not "is connected": the finish-setup stage also covers an installed
 * bridge that is unhealthy or has not confirmed both channels.
 */
export type BridgeUpgradeFinishStage = Exclude<BridgeUpgradeStage, "start">;

export const bridgeUpgradeFinishCopy = (
    stage: BridgeUpgradeFinishStage,
    integration: BridgeIntegrationId | null,
): { lead: string; detail: string; cta: string } =>
    stage === "finishInstall"
        ? {
              lead: "Finish installing HappyBridge",
              detail: `Run the HappyBridge installer on the computer that hosts ${softwarePossessive(integration)}'s database to finish connecting.`,
              cta: "Finish installing",
          }
        : {
              lead: "Finish setting up HappyBridge",
              detail: "A few steps left: choose which rooms or staff show in HappyDoc and how their names appear, so your appointments come through right.",
              cta: "Finish upgrade",
          };
